import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Form, Button, Message, Responsive } from 'semantic-ui-react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { FormComponents, Loader } from '../components';
import { ApiStatuses } from '../api';
import { Breakpoints, Validations } from '../utils';

class ManualsFormComponent extends PureComponent {
  state = { values: null, families: null };

  static propTypes = {
    children: PropTypes.node,
    intl: intlShape.isRequired,
    // for dropdown options
    brand: PropTypes.array,
    brandStatus: PropTypes.string,
    brandError: PropTypes.string,
    manualType: PropTypes.array,
    language: PropTypes.array,
    productFamily: PropTypes.array,

    header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    error: PropTypes.string,
    submitting: PropTypes.bool,
    onHandleSubmit: PropTypes.func,
    onHandleOnChangeBrand: PropTypes.func,
    preSelect: PropTypes.string
  };

  componentDidMount() {
    // If there's a brand passed in the URL, then we try and pre-populate some of the form fields
    const { preSelect, onHandleOnChangeBrand } = this.props;
    if (preSelect) {
      const preSelectedBrand = this.getBrand(preSelect);
      if (preSelectedBrand) {
        onHandleOnChangeBrand(preSelectedBrand.BrandId);
      }
    }
  }

  getBrand = name => {
    if (!name) {
      return {};
    }

    const { brand } = this.props;
    // eslint-disable-next-line
    const requestedBrandIndex = brand.findIndex(b => {
      return (
        b.Name.toUpperCase() === name.toUpperCase() ||
        b.Name.split(' ')
          .join('')
          .toUpperCase() === name.toUpperCase()
      );
    });
    return requestedBrandIndex > -1 ? brand[requestedBrandIndex] : {};
  };

  onSubmitOptions = (values, FormikBag) => {
    const { onHandleSubmit } = this.props;
    const { resetForm, setSubmitting } = FormikBag;
    setSubmitting(true);
    onHandleSubmit(values);
    // for visual submitting state
    setTimeout(() => {
      setSubmitting(false);
      resetForm(values);
    }, 1000);
  };

  // TODO: check requirements
  validationSchema = () => {
    const schema = {
      Brand: yup.string().required(Validations.getErrorMessage('required'))
    };

    return yup.object().shape(schema);
  };

  render() {
    const {
      error,
      brand,
      brandStatus,
      brandError,
      manualType,
      language,
      productFamily,
      intl,
      onHandleOnChangeBrand,
      preSelect
    } = this.props;

    const initBrand = this.getBrand(preSelect);
    // language IDs changable, so manually look for the word `English` form the name
    const initLang =
      language && language.find(({ Name }) => Name.indexOf('English') >= 0);
    return (
      <Formik
        onSubmit={this.onSubmitOptions}
        validationSchema={this.validationSchema()}
        initialValues={{
          Brand: initBrand.BrandId || '',
          ProductFamily: '',
          Language: (initLang && initLang.LanguageId) || '',
          Keywords: '',
          ManualType: ''
        }}
        enableReinitialize={true}
        isInitialValid={!!initBrand.BrandId}
      >
        {({
          handleSubmit,
          isSubmitting,
          handleChange,
          setValues,
          isValid,
          values
        }) => (
          <Form
            onSubmit={handleSubmit}
            error={error}
            className="bg--secondary"
            data-testid="manuals-form-form"
            data-qa="manuals-form"
          >
            <Form.Group className="fields--fluid">
              {brandStatus === ApiStatuses.Loading && <Loader active />}

              {brand && brand.length > 1 && (
                <FormComponents.Select
                  name="Brand"
                  placeholder={intl.formatMessage({
                    id: 'form.select',
                    defaultMessage: 'Select'
                  })}
                  label={intl.formatMessage({
                    id: 'form.product_brand',
                    defaultMessage: 'Product Brand'
                  })}
                  options={brand.map(({ BrandId, Name }) => ({
                    value: BrandId,
                    text: Name
                  }))}
                  onChange={e => {
                    const {
                      target: { value }
                    } = e;
                    // call formik handleChange to update values
                    setValues({ ...values, ProductFamily: '' });
                    handleChange(e);
                    // call wrapper handleOnChange to update snapshot
                    onHandleOnChangeBrand(value);
                  }}
                />
              )}

              {productFamily && (
                <FormComponents.Select
                  name="ProductFamily"
                  placeholder={intl.formatMessage({
                    id: 'form.select',
                    defaultMessage: 'Select'
                  })}
                  label={intl.formatMessage({
                    id: 'form.product_family',
                    defaultMessage: 'Product Family'
                  })}
                  options={productFamily.map(family => ({
                    value: family.ProductFamilyId,
                    text: family.Name
                  }))}
                  onChange={handleChange('ProductFamily')}
                  placeholderDisabled={false}
                />
              )}

              {manualType && (
                <FormComponents.Select
                  name="ManualType"
                  placeholder={intl.formatMessage({
                    id: 'form.select',
                    defaultMessage: 'Select'
                  })}
                  label={intl.formatMessage({
                    id: 'form.manual_type',
                    defaultMessage: 'Manual Type'
                  })}
                  options={manualType.map(type => ({
                    value: type.ManualTypeId,
                    text: type.Name
                  }))}
                  onChange={handleChange('ManualType')}
                  placeholderDisabled={false}
                />
              )}

              {language && (
                <FormComponents.Select
                  name="Language"
                  placeholder={intl.formatMessage({
                    id: 'form.select',
                    defaultMessage: 'Select'
                  })}
                  label={intl.formatMessage({
                    id: 'form.language',
                    defaultMessage: 'Language'
                  })}
                  options={language.map(lang => ({
                    value: lang.LanguageId,
                    text: lang.Name
                  }))}
                  onChange={handleChange('Language')}
                  placeholderDisabled={false}
                />
              )}

              <FormComponents.Field
                name="Keywords"
                label={
                  <FormattedMessage
                    id="form.search_text"
                    description="Search Text field label"
                    defaultMessage="Search Text"
                  />
                }
              />

              {brandStatus === ApiStatuses.Error && (
                <Message visible error header={brandError} icon="ban" />
              )}
              <FormComponents.FormError error={error} />
              {brandStatus === ApiStatuses.Loaded && (
                <Form.Field
                  data-qa="submit-adjust-button"
                  as="footer"
                  type="submit"
                  control={Button}
                  size="small"
                  primary
                  className="field--center field--flex"
                  disabled={isSubmitting || !isValid}
                >
                  <Responsive
                    as={Fragment}
                    maxWidth={Breakpoints.computerBreakpoint - 1}
                  >
                    <FormattedMessage id="cta.search" defaultMessage="Search" />
                  </Responsive>
                  <Responsive
                    as={Fragment}
                    minWidth={Breakpoints.computerBreakpoint}
                  >
                    <FormattedMessage id="cta.go" defaultMessage="Go" />
                  </Responsive>
                </Form.Field>
              )}
            </Form.Group>
          </Form>
        )}
      </Formik>
    );
  }
}

export default injectIntl(ManualsFormComponent);



// WEBPACK FOOTER //
// ./src/forms/ManualsForm.js